<template>
  <WdsButtonGhost
    class="hire-column-worker-information"
    data-test="hire-column-worker"
    @click="openPanel"
  >
    <WdsAvatarLabel :path="avatarPath" truncated>
      <WdsLabel
        :gray="column.activeStatus === HireActiveStatus.Draft"
        :success="column.activeStatus === HireActiveStatus.Active"
        :warning="column.activeStatus === HireActiveStatus.Ended"
        :primary="[HireActiveStatus.Signed, HireActiveStatus.Offered].includes(column.activeStatus)"
        :error="
          [HireActiveStatus.Cancelled, HireActiveStatus.Terminated].includes(column.activeStatus)
        "
        subtle
        xs
      >
        {{ convertEnumToDisplayText(column.activeStatus) }}
      </WdsLabel>
      <template #subText>
        <WdsStack direction-row align-center gap-sm>
          <WdsText primary>
            {{ column.worker.name }}
          </WdsText>

          <WdsTooltip
            v-if="
              column?.recruiterOwnershipDays &&
              column?.recruiterOwnershipDays > 0 &&
              column?.recruiterOwnershipDaysLeft &&
              column?.recruiterOwnershipDaysLeft > 0
            "
            top
            global
            data-test="hire-worker-managed-status"
          >
            <div><WdsIcon color="#0E0D12" name="group" weight="thick" :size="12" /></div>

            <template #tooltip-content>
              <WdsStack direction-column gap-sm>
                <WdsStack direction-column>
                  <div v-if="column?.recruiterManagesWorkers">{{ $t('Worker managed by') }}</div>
                  <div v-else>{{ $t('Ongoing ownership period for') }}</div>
                  <div><strong>{{ column?.recruiter?.name }}</strong></div>
                </WdsStack>
              </WdsStack>
            </template>
          </WdsTooltip>
        </WdsStack>
      </template>
    </WdsAvatarLabel>
  </WdsButtonGhost>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import {
  WdsStack,
  WdsAvatarLabel,
  WdsButtonGhost,
  WdsLabel,
  WdsText,
  WdsTooltip,
  WdsIcon,
} from '@worksome/wds-components'
import { FragmentType, graphql, useFragment } from '@/gql'
import { convertEnumToDisplayText } from '@/utils/tools'
import { HireActiveStatus } from '@/gql/graphql'
import { openWorkerDetailPanel } from '@/components/GraphqlDataTable/listners'
import { getAvatarPath } from '@/utils/avatar'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

const HireAvatarColumn = graphql(`
  fragment HireAvatarColumn on Hire {
    id
    recruiter {
      name
    }

    recruiterFee
    recruiterOwnershipStartDate
    recruiterOwnershipDays
    recruiterOwnershipDaysLeft
    recruiterManagesWorkers

    worker {
      id
      name
      avatar
      email # for export purposes
      # Also we need a way for handling multiple accounts dynamically
      # viewerIsClient # Would really like a way to get the TC's information directly via the worker information
    }
    activeStatus
  }
`)

const props = defineProps<{ row: FragmentType<typeof HireAvatarColumn> }>()
const column = computed(() => useFragment(HireAvatarColumn, props.row))

const openPanel = () => {
  openWorkerDetailPanel({
    workerAccountId: column.value.worker.id,
    // Update the viewer account id here once we have a multi account setup to the selected accounts?
    viewerAccountId: currentAccount.globalId,
  })
}

const avatarPath = computed(() => getAvatarPath(column.value.worker.avatar, column.value.worker.id))
</script>

<style lang="scss">
.hire-column-worker-information {
  text-align: left;
}
</style>
